import cx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as LogsIcon } from '@assets/svg/ic-equipment-logs.svg';
import { ReactComponent as LegendIcon } from '@assets/svg/ic-legendSwitch.svg';

import Tabs from '@components/uikit/Tabs/Tabs';

import useTranslation from '@hooks/useTranslation';

import type { TEquipmentStatus } from '../../model/equipmentsList';
import {
  selectManageEquipmentExtraPanel,
  useManageEquipmentActions,
} from '../../store/useManageEquipmentStore';
import SensorTypeSummaryPanel from '../SensorTypeSummaryPanel/SensorTypeSummaryPanel';
import TableEquipments from '../TableEquipments/TableEquipments';
import TableEquipmentsLog from '../TableEquipmentsLog/TableEquipmentsLog';
import style from './style.module.scss';

const EquipmentBlock = () => {
  const { t } = useTranslation();

  const equipmentStatusList: Array<{ value: TEquipmentStatus; label: string }> = [
    { value: 'all', label: t('All') },
    { value: 'active', label: t('Active') },
    { value: 'inactive', label: t('Inactive') },
  ];

  const [equipmentStatus, setEquipmentStatus] = useState<TEquipmentStatus>(equipmentStatusList[0].value);

  const extraPanel = selectManageEquipmentExtraPanel();

  const {
    setEquipmentSelectedRows,
    setEquipmentSelectedAll,
    setActiveEquipment,
    setInactiveEquipment,
    setEquipmentExtraPanel,
  } = useManageEquipmentActions();

  const resetState = () => {
    setEquipmentSelectedRows({});
    setEquipmentSelectedAll(false);
    setActiveEquipment([]);
    setInactiveEquipment([]);
  };

  const handleChangeExtraPanel = (value: string) => {
    setEquipmentExtraPanel(value);
  };

  const handleChangeEquipmentStatus = (value: TEquipmentStatus) => {
    setEquipmentStatus(value);
    resetState();
  };

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  return (
    <div className={style.equipmentBlock}>
      <div>
        <div className={style.equipmentPanelContainer}>
          <div className={style.equipmentPanelFilter}>
            <Tabs
              label={t('Equipment status')}
              value={equipmentStatus}
              tabsItems={equipmentStatusList}
              type="woHelperText"
              onChange={handleChangeEquipmentStatus}
              itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
            />
          </div>

          <div className={style.equipmentPanelBtns}>
            <div
              className={cx(style.switchItem, { [style.activeItem]: extraPanel === 'summary' })}
              onClick={() => handleChangeExtraPanel('summary')}
              id="info-icon"
              data-tooltip-content={t('View status legend')}
            >
              <LegendIcon />
            </div>
            <div
              className={cx(style.switchItem, { [style.activeItem]: extraPanel === 'logs' })}
              onClick={() => handleChangeExtraPanel('logs')}
              id="info-icon"
              data-tooltip-content={t('Show Equipment Change Log')}
            >
              <LogsIcon className={style.logsIcon} />
            </div>
          </div>
        </div>
        {extraPanel === 'logs' && <TableEquipmentsLog />}
        {extraPanel === 'summary' && <SensorTypeSummaryPanel />}
      </div>

      <TableEquipments equipmentStatusValue={equipmentStatus} />
      <Tooltip anchorSelect="#info-icon" content={t('Original time based on facility time zone')} />
    </div>
  );
};

export default EquipmentBlock;
